import type { AxiosError } from 'axios';

/** Stable machine codes from the account API envelope (API_MICROAPPS.md §9). */
export type ElvaErrorCode =
  | 'not_authenticated'
  | 'authentication_failed'
  | 'invalid_refresh_token'
  | 'session_revoked'
  | 'user_unavailable'
  | 'invalid_code'
  | 'invalid_client'
  | 'app_not_permitted'
  | 'location_required'
  | 'location_not_in_scope'
  | 'not_found'
  | 'too_many_attempts'
  | 'validation_error'
  | 'method_not_allowed'
  | 'storage_unavailable'
  | 'upload_incomplete';

export interface ElvaApiError {
  status: number;
  /** Compare against `ElvaErrorCode`; unknown codes are passed through verbatim. */
  code: string;
  message: string;
  errors?: Record<string, string[]>;
}

interface FailureEnvelope {
  statusCode?: number;
  error?: unknown;
  message?: unknown;
  errors?: unknown;
}

export function readApiError(error: unknown): ElvaApiError | null {
  const response = (error as AxiosError<FailureEnvelope> | undefined)?.response;
  const body = response?.data;

  if (!body || typeof body !== 'object' || typeof body.error !== 'string') {
    return null;
  }

  const result: ElvaApiError = {
    status: response.status,
    code: body.error,
    message: typeof body.message === 'string' ? body.message : '',
  };

  if (body.errors && typeof body.errors === 'object') {
    result.errors = body.errors as Record<string, string[]>;
  }

  return result;
}

export function isApiError(error: unknown, code: ElvaErrorCode): boolean {
  return readApiError(error)?.code === code;
}

/**
 * `authentication_failed` covers three cases and only one is retryable:
 * `Token has expired.` → refresh; `Invalid token.` / `Token has been revoked.` → sign in again.
 */
export function isExpiredTokenError(apiError: ElvaApiError | null): boolean {
  return (
    apiError?.code === 'authentication_failed' &&
    apiError.message.toLowerCase().includes('expired')
  );
}

const SIGN_IN_AGAIN_CODES = new Set<string>([
  'invalid_refresh_token',
  'session_revoked',
  'user_unavailable',
]);

export function requiresFreshSignIn(apiError: ElvaApiError | null): boolean {
  if (!apiError) return false;
  if (SIGN_IN_AGAIN_CODES.has(apiError.code)) return true;
  return apiError.code === 'authentication_failed' && !isExpiredTokenError(apiError);
}
